import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import { api, rateLimitMessage } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Network as NetworkIcon, ArrowRight, X, Wand2 } from "lucide-react";

const REGION_COLOR = {
    EU: "#3B82F6", US: "#F59E0B", Asia: "#10B981", LATAM: "#EC4899", MEA: "#A855F7", Global: "#06B6D4",
};
const NODE_DEFAULT = "#71717A";

// Deterministic theme → color: hash the theme string into one of 12 distinct hues.
const THEME_PALETTE = [
    "#F59E0B", "#3B82F6", "#10B981", "#EC4899", "#A855F7",
    "#06B6D4", "#F97316", "#84CC16", "#EF4444", "#8B5CF6",
    "#14B8A6", "#FBBF24",
];
function themeColor(theme) {
    if (!theme) return NODE_DEFAULT;
    let h = 0;
    for (let i = 0; i < theme.length; i++) h = (h * 31 + theme.charCodeAt(i)) >>> 0;
    return THEME_PALETTE[h % THEME_PALETTE.length];
}

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

/**
 * Force-directed graph of the user's firm library.
 * - Nodes = firms (sized by total annual revenue, colored by region)
 * - Edges = saved synergies (alpha + thickness by fit_score)
 * - Click node → side panel with firm info + link
 * - Click edge → open the synergy
 */
export default function Network() {
    const containerRef = useRef(null);
    const fgRef = useRef(null);
    const [firms, setFirms] = useState(null);
    const [synergies, setSynergies] = useState(null);
    const [dims, setDims] = useState({ w: 800, h: 600 });
    const [selected, setSelected] = useState(null);
    const [colorMode, setColorMode] = useState("region"); // "region" | "themes"
    const [tagging, setTagging] = useState(false);
    const [tagError, setTagError] = useState("");
    const [tagMsg, setTagMsg] = useState("");

    const loadAll = async () => {
        const [f, s] = await Promise.all([api.get("/firms"), api.get("/synergies")]);
        setFirms(f.data || []);
        setSynergies(s.data || []);
    };

    useEffect(() => { loadAll(); }, []);

    const tagThemes = async () => {
        setTagging(true); setTagError(""); setTagMsg("");
        try {
            const { data } = await api.post("/firms/auto-themes/bulk");
            setTagMsg(`Tagged ${data.updated}/${data.total} firms.`);
            await loadAll();
            setColorMode("themes");
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setTagError(rl || (typeof d === "string" ? d : "Tagging failed"));
        } finally {
            setTagging(false);
        }
    };

    useEffect(() => {
        const ro = new ResizeObserver(() => {
            if (containerRef.current) {
                setDims({
                    w: containerRef.current.clientWidth,
                    h: Math.max(500, window.innerHeight - 220),
                });
            }
        });
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const graph = useMemo(() => {
        if (!firms || !synergies) return null;
        const revOf = (f) => (f.products || []).reduce((a, p) => a + (Number(p.annual_revenue_usd) || 0), 0);
        const maxRev = Math.max(1, ...firms.map(revOf));
        // Firms that are FIRM_A on at least one saved synergy with fit ≥ 90 → "hot opportunities".
        const hotFirms = new Set((synergies || []).filter((s) => (s.fit_score || 0) >= 90).map((s) => s.firm_a_id));
        const nodes = firms.map((f) => {
            const primaryTheme = (f.themes || [])[0];
            const regionCol = REGION_COLOR[f.region] || NODE_DEFAULT;
            const themeCol = primaryTheme ? themeColor(primaryTheme) : NODE_DEFAULT;
            const firmCol = f.color || NODE_DEFAULT;
            let color;
            if (colorMode === "multi") color = themeCol; // primary fill; outer ring + center dot drawn separately
            else if (colorMode === "firm") color = firmCol;
            else if (colorMode === "themes") color = themeCol;
            else color = regionCol;
            return {
                id: f.id,
                name: f.name,
                region: f.region || "Other",
                industry: f.industry || "",
                themes: f.themes || [],
                revenue: revOf(f),
                val: 6 + 18 * Math.sqrt(revOf(f) / maxRev),
                color,
                regionColor: regionCol,
                themeColor: themeCol,
                firmColor: firmCol,
                isHot: hotFirms.has(f.id),
            };
        });
        const links = synergies.filter((s) => s.fit_score >= 50).map((s) => ({
            source: s.firm_a_id,
            target: s.firm_b_id,
            fit: s.fit_score,
            projected: s.projected_revenue,
            product_id: s.product_id,
            product_name: s.product_name,
        }));
        return { nodes, links };
    }, [firms, synergies, colorMode]);

    // Keep canvas redrawing while there are hot opportunities (firm_a with fit ≥ 90),
    // so their pulsing halo can animate even after the force simulation settles.
    const hasHotNodes = !!graph?.nodes?.some?.((n) => n.isHot);
    useEffect(() => {
        if (!hasHotNodes) return;
        let raf;
        const loop = () => {
            if (fgRef.current && fgRef.current.refresh) fgRef.current.refresh();
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [hasHotNodes]);


    // Build a legend keyed by mode: regions used in this dataset OR top themes OR firm names.
    const legend = useMemo(() => {
        if (!firms) return [];
        if (colorMode === "themes") {
            const counts = new Map();
            firms.forEach((f) => (f.themes || []).slice(0, 1).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
            return [...counts.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([t, n]) => ({ label: `${t} (${n})`, color: themeColor(t) }));
        }
        if (colorMode === "firm") {
            return firms
                .filter((f) => f.color)
                .slice(0, 12)
                .map((f) => ({ label: f.name, color: f.color }));
        }
        if (colorMode === "multi") {
            // 3-row mini-legend explaining the concentric rings
            return [
                { label: "outer ring = region", color: "#71717A", swatch: "ring" },
                { label: "inner fill = theme", color: "#71717A", swatch: "fill" },
                { label: "center dot = brand", color: "#71717A", swatch: "dot" },
            ];
        }
        const usedRegions = new Set(firms.map((f) => f.region).filter(Boolean));
        return Object.entries(REGION_COLOR)
            .filter(([r]) => usedRegions.has(r))
            .map(([r, c]) => ({ label: r, color: c }));
    }, [firms, colorMode]);

    const onNodeClick = (n) => {
        setSelected(n);
        if (fgRef.current) {
            fgRef.current.centerAt(n.x, n.y, 600);
            fgRef.current.zoom(2.4, 600);
        }
    };

    const loading = !firms || !synergies;

    return (
        <div className="p-8" data-testid="network-page">
            <header className="mb-6 border-b border-zinc-800 pb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Network</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Library network graph</h1>
                    <p className="text-sm text-zinc-500 mt-2">
                        Firms sized by revenue, colored by {colorMode === "themes" ? "strategic theme cluster" : colorMode === "firm" ? "user-chosen brand color" : colorMode === "multi" ? "all three axes (region ring · theme fill · brand dot)" : "region"}. Edges connect firms with saved synergies (fit ≥ 50) — thicker = better fit.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex border border-zinc-800" role="tablist" data-testid="color-mode-toggle">
                        {["region", "themes", "firm", "multi"].map((m) => (
                            <button
                                key={m}
                                onClick={() => setColorMode(m)}
                                data-testid={`color-mode-${m}`}
                                className={`text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 transition-colors ${
                                    colorMode === m ? "bg-amber-500 text-black font-bold" : "text-zinc-400 hover:bg-zinc-900"
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={tagThemes}
                        disabled={tagging}
                        data-testid="tag-themes-btn"
                        title="Use AI to tag every firm in the library with 2-3 strategic themes (consistent across firms)"
                        className="inline-flex items-center gap-1.5 border border-amber-500/60 hover:border-amber-400 bg-amber-500/10 hover:bg-amber-500/20 text-xs font-mono uppercase tracking-wider text-amber-400 px-2.5 py-1.5 transition-colors disabled:opacity-50"
                    >
                        <Wand2 className="w-3 h-3" /> {tagging ? "Tagging…" : "Tag themes"}
                    </button>
                </div>
            </header>

            {(tagError || tagMsg) && (
                <div data-testid="tag-status" className={`mb-4 border px-3 py-2 font-mono text-xs ${tagError ? "border-red-900/60 bg-red-950/30 text-red-400" : "border-emerald-700/60 bg-emerald-950/30 text-emerald-300"}`}>
                    {tagError || tagMsg}
                </div>
            )}

            {/* Color legend (dynamic per mode) */}
            <div className="mb-4 flex flex-wrap items-center gap-3 font-mono text-[10px] text-zinc-500" data-testid="network-legend">
                {legend.length === 0 ? (
                    <span className="text-zinc-700">{colorMode === "themes" ? "No themes tagged yet — click \"Tag themes\"." : "No firms yet."}</span>
                ) : legend.map((row) => (
                    <div key={row.label} className="inline-flex items-center gap-1.5">
                        {row.swatch === "ring" ? (
                            <span className="w-3 h-3 rounded-full inline-block border-2" style={{ borderColor: "#fafafa", background: "transparent" }} />
                        ) : row.swatch === "dot" ? (
                            <span className="w-3 h-3 rounded-full inline-block flex items-center justify-center" style={{ background: "#27272A" }}>
                                <span className="w-1 h-1 rounded-full inline-block" style={{ background: "#fafafa" }} />
                            </span>
                        ) : (
                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: row.color }} />
                        )}
                        <span>{row.label}</span>
                    </div>
                ))}
                <div className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: NODE_DEFAULT }} />
                    <span>{colorMode === "themes" ? "untagged" : "Other / —"}</span>
                </div>
                {hasHotNodes && (
                    <div className="inline-flex items-center gap-1.5 ml-2 pl-3 border-l border-zinc-800" data-testid="hot-legend">
                        <span className="w-2.5 h-2.5 rounded-full inline-block border-2 border-emerald-400 bg-transparent animate-pulse" />
                        <span className="text-emerald-400">hot opportunity (fit ≥ 90)</span>
                    </div>
                )}
            </div>

            <div
                ref={containerRef}
                className="border border-zinc-800 bg-zinc-950 relative overflow-hidden"
                style={{ minHeight: 500 }}
                data-testid="network-canvas"
            >
                {loading ? (
                    <div className="p-12"><Loader label="Loading network" /></div>
                ) : graph.nodes.length === 0 ? (
                    <div className="p-16 text-center">
                        <NetworkIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                        <div className="font-heading font-bold text-zinc-300 text-lg mb-2">Library is empty</div>
                        <div className="text-sm text-zinc-500">
                            <Link to="/app/research" className="text-amber-500 hover:underline">Research a firm</Link> first.
                        </div>
                    </div>
                ) : (
                    <ForceGraph2D
                        ref={fgRef}
                        graphData={graph}
                        width={dims.w}
                        height={dims.h}
                        backgroundColor="#09090B"
                        nodeRelSize={1}
                        nodeLabel={(n) => `${n.name} · ${n.industry} · ${n.region}${n.themes?.length ? " · " + n.themes.join(", ") : ""} · ${fmtMoney(n.revenue)}`}
                        nodeCanvasObject={(node, ctx, globalScale) => {
                            const label = node.name;
                            const r = node.val;
                            // Pulsing emerald halo for "hot opportunity" firms (firm_a with fit ≥ 90).
                            if (node.isHot) {
                                const t = (performance.now() % 1800) / 1800;
                                const pulse = (Math.sin(t * Math.PI * 2) + 1) / 2; // 0..1
                                const haloR = r * (1.45 + pulse * 0.35);
                                ctx.beginPath();
                                ctx.arc(node.x, node.y, haloR, 0, Math.PI * 2);
                                ctx.strokeStyle = `rgba(16, 185, 129, ${0.18 + pulse * 0.42})`;
                                ctx.lineWidth = (2.5 + pulse * 1.5) / globalScale;
                                ctx.stroke();
                            }
                            if (colorMode === "multi") {
                                // Tri-color: outer ring = region, inner fill = theme, center dot = brand
                                ctx.beginPath();
                                ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
                                ctx.fillStyle = node.themeColor || NODE_DEFAULT;
                                ctx.fill();
                                ctx.lineWidth = Math.max(2, r * 0.22);
                                ctx.strokeStyle = node.regionColor || NODE_DEFAULT;
                                ctx.stroke();
                                if (node.firmColor && node.firmColor !== NODE_DEFAULT) {
                                    ctx.beginPath();
                                    ctx.arc(node.x, node.y, Math.max(2, r * 0.32), 0, 2 * Math.PI, false);
                                    ctx.fillStyle = node.firmColor;
                                    ctx.fill();
                                    ctx.lineWidth = 0.6 / globalScale;
                                    ctx.strokeStyle = "rgba(0,0,0,0.6)";
                                    ctx.stroke();
                                }
                            } else {
                                ctx.beginPath();
                                ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
                                ctx.fillStyle = node.color;
                                ctx.fill();
                                ctx.strokeStyle = "rgba(255,255,255,0.15)";
                                ctx.lineWidth = 1.5 / globalScale;
                                ctx.stroke();
                            }
                            if (globalScale > 1.2) {
                                const fontSize = 12 / globalScale;
                                ctx.font = `${fontSize}px "JetBrains Mono", monospace`;
                                ctx.fillStyle = "#fafafa";
                                ctx.textAlign = "center";
                                ctx.textBaseline = "top";
                                ctx.fillText(label, node.x, node.y + r + 4 / globalScale);
                            }
                        }}
                        linkColor={(l) => `rgba(245,158,11,${Math.max(0.15, (l.fit || 50) / 110)})`}
                        linkWidth={(l) => Math.max(0.5, (l.fit || 50) / 30)}
                        linkLabel={(l) => `Fit ${l.fit} · ${l.product_name} · ${fmtMoney(l.projected)}`}
                        onNodeClick={onNodeClick}
                        onLinkClick={(l) => {
                            const sid = l.source?.id || l.source;
                            const tid = l.target?.id || l.target;
                            window.open(`/app/synergy?key=${encodeURIComponent(`${sid}:${tid}:${l.product_id}`)}`, "_blank");
                        }}
                        cooldownTicks={120}
                    />
                )}
                {/* Detail card */}
                {selected && (
                    <div data-testid="network-selected-panel" className="absolute right-4 top-4 w-72 bg-black/90 border border-zinc-800 backdrop-blur p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                                <div className="font-heading font-bold text-zinc-50 text-base truncate">{selected.name}</div>
                                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{selected.industry || "—"} · {selected.region}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-zinc-100"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        <div className="text-xs text-zinc-400 font-mono mb-3">Total revenue: <span className="text-amber-400">{fmtMoney(selected.revenue)}</span></div>
                        {selected.themes?.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-1">
                                {selected.themes.map((t) => (
                                    <span key={t} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: themeColor(t) + "60", color: themeColor(t) }}>{t}</span>
                                ))}
                            </div>
                        )}
                        {selected.isHot && (
                            <div data-testid="hot-badge" className="mb-3 inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-mono text-[10px] uppercase tracking-wider px-2 py-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Hot opportunity
                            </div>
                        )}
                        <div className="flex flex-col gap-1.5">
                            <Link to={`/app/firms/${selected.id}`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-300">
                                Open firm <ArrowRight className="w-3 h-3" />
                            </Link>
                            <Link to={`/app/firms/${selected.id}/partners`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-300">
                                Find partners <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
